import { DateTime } from "luxon";
import { useDateFromHashOrNow } from "../../hooks/useDateFromHashOrNow";
import { useCallback, useMemo, useRef, useState } from "react";
import deLocale from "@fullcalendar/core/locales/de";
import { type EventInput } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton, ListItemIcon, Menu, MenuItem, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { EventContent } from "../../misc/calendar/CalendarEventCardContent";
import { AutovioCalendarEvent } from "../../model/autovioCalendarEvents";
import { autovioCalendarEventToEventInput, calendarSlotLabelFormat, eventClassNames } from "../../utils/calendar";
import { AddAppointmentDialog } from "../../dialogs/AddAppointmentDialog/AddAppointmentDialog";
import FullCalendar from "@fullcalendar/react";
import { Column } from "../../misc/Column";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { useDialog } from "../../hooks/useDialog";
import { useGetManyReference, useGetRecordId, useRecordContext } from "react-admin";
import { useRefetchCalendarEventsCounter } from "../../hooks/useRefetchCalendarEventsCounter";
import { useAutovioContext } from "../../hooks/useAutovioContext";
import { isSimulator, type Simulator, type Vehicle } from "../../providers/vehiclesProvider";
import { WeekdayEnum } from "../../model/Weekday";
import { DateRange } from "../../model/DateRange";
import { DateWithoutTime } from "../../model/DateWithoutTime";
import { useRecoilState } from "recoil";
import { persistentCalendarViewStateFamily } from "../../persistent_state";
import { useNegativeBalanceWarnings } from "../../hooks/useNegativeBalanceWarnings";
import { CalendarDayHeaderContent } from "../../misc/calendar/CalendarDayHeaderContent";
import { overviewCardStyle } from "../../misc/backofficeTheme";
import { useMenu } from "../../hooks/useMenu";
import { grants } from "../../backoffice.access_control";

type VehicleCalendarView = "timeGridDay" | "timeGridWeek" | "dayGridMonth";

const calendarViewChoices = [
  { id: "timeGridDay", name: "Tag" },
  { id: "timeGridWeek", name: "Woche" },
  { id: "dayGridMonth", name: "Monat" },
] as const satisfies Array<{ id: VehicleCalendarView; name: string }>;

export function VehicleCalendar() {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const calendarRef = useRef<FullCalendar>(null);
  const [{ drivingSchoolId }] = useAutovioContext();
  const vehicleId = useGetRecordId();
  const vehicle = useRecordContext<Vehicle>();
  const initialDate = useDateFromHashOrNow();
  const [calendarTitle, setCalendarTitle] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>([
    DateWithoutTime.fromDateTime(initialDate.startOf("week")),
    DateWithoutTime.fromDateTime(initialDate.endOf("week")),
  ]);
  const refetchCounter = useRefetchCalendarEventsCounter();
  const { data, isLoading, error } = useGetManyReference<AutovioCalendarEvent>(
    "calendarEvents",
    {
      target: "vehicleIds",
      id: vehicleId,
      filter: { dateRange, drivingSchoolId },
      pagination: { page: 1, perPage: 9999 },
      meta: { refetchCounter },
      sort: { field: "start", order: "ASC" },
    },
    {
      enabled: !!(vehicleId && drivingSchoolId),
    },
  );
  const minHour = data?.reduce((acc, it) => Math.min(acc, it.start.hour), 6) ?? 6;
  const { dialogProps, openDialog } = useDialog();
  const [persistedView, setPersistedView] = useRecoilState(persistentCalendarViewStateFamily("ResourceCalendar"));
  const { warningsByDay, warningsByEventId } = useNegativeBalanceWarnings(data);
  const changeCalendarView = useCallback((view: VehicleCalendarView) => {
    calendarRef.current?.getApi().changeView(view);
  }, []);

  if (!drivingSchoolId) {
    return null;
  }

  const events = useMemo(() => {
    let events: Array<EventInput> =
      data?.map((event) => {
        const warning = warningsByEventId.get(event.id);
        return autovioCalendarEventToEventInput(event, warning ? { warnings: [warning] } : {});
      }) ?? [];
    if (isSimulator(vehicle)) {
      events = events.concat(_simulatorAvailabilityEvents(vehicle, dateRange));
    }
    return events;
  }, [data, warningsByEventId, vehicle, dateRange]);

  const creationTabs = _authorizedCreationTabs(vehicle);
  const canCreateAppointment = creationTabs.length > 0;

  return (
    <Column className="vehicle-calendar" data-testid="VehicleCalendar">
      <Box data-testid="vehicle-calendar-card" sx={{ ...overviewCardStyle, p: 3, mt: 0 }}>
        <div className="vehicle-calendar-card-header">
          <h2 className="fc-toolbar-title vehicle-calendar-title">{calendarTitle}</h2>
          <div className="vehicle-calendar-mobile-header-menu">
            {canCreateAppointment && <VehicleCalendarCreateMenuButton openDialog={openDialog} />}
            <VehicleCalendarHeaderMenuButton
              activeCalendarView={persistedView ?? "timeGridWeek"}
              changeCalendarView={changeCalendarView}
            />
          </div>
        </div>
        <FullCalendar
          ref={calendarRef}
          timeZone="Europe/Berlin"
          locales={[deLocale]}
          height="auto"
          slotMinTime={`0${minHour}:00:00`}
          slotLabelFormat={calendarSlotLabelFormat}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          eventMinHeight={1}
          headerToolbar={{
            left: "title",
            center: "",
            right: `prev today next ${!isMobile && canCreateAppointment ? "addAppointmentButton " : ""}timeGridDay,timeGridWeek,dayGridMonth`,
          }}
          customButtons={{
            addAppointmentButton: { text: "＋\u00A0Termin", click: openDialog, hint: "Neuen Termin hinzufügen" },
          }}
          initialDate={initialDate.toISO()}
          initialView={persistedView ?? "timeGridWeek"}
          scrollTime="08:00:00"
          allDaySlot={false}
          editable={false}
          weekNumbers={true}
          showNonCurrentDates={false}
          dayMaxEvents={true}
          nowIndicator={true}
          events={events}
          eventClassNames={eventClassNames}
          eventContent={(eventInfo) => <EventContent eventInfo={eventInfo} mode="ResourceCalendar" />}
          dayHeaderContent={(arg) => <CalendarDayHeaderContent arg={arg} warningsByDay={warningsByDay} />}
          datesSet={(dates) => {
            setCalendarTitle(dates.view.title);
            if (dates.view?.type && persistedView !== dates.view.type) {
              setPersistedView(dates.view.type);
            }
            const start = DateWithoutTime.fromDateTime(DateTime.fromJSDate(dates.start, { zone: "Europe/Berlin" }));
            const end = DateWithoutTime.fromDateTime(DateTime.fromJSDate(dates.end, { zone: "Europe/Berlin" })).prevDay;
            if (dateRange[0].toString() !== start.toString() || dateRange[1].toString() !== end.toString()) {
              setDateRange([start, end]);
            }
          }}
          dateClick={canCreateAppointment ? AddAppointmentDialog.fullCalendarDateClickHandler(openDialog) : undefined}
        />
      </Box>
      <AddAppointmentDialog {...dialogProps} vehicle={vehicle} tabs={creationTabs} />
      {(isLoading || error || !vehicle) && <LoadingIndicator />}
    </Column>
  );
}

function VehicleCalendarCreateMenuButton(props: { openDialog: () => void }) {
  const { openDialog } = props;
  const { anchorEl, open, openMenu, closeMenu } = useMenu();

  return (
    <div>
      <IconButton size="small" onClick={openMenu} aria-label="Fahrzeugtermin hinzufügen">
        <AddIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            closeMenu();
            openDialog();
          }}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          Termin
        </MenuItem>
      </Menu>
    </div>
  );
}

function VehicleCalendarHeaderMenuButton(props: {
  activeCalendarView: string;
  changeCalendarView: (view: VehicleCalendarView) => void;
}) {
  const { activeCalendarView, changeCalendarView } = props;
  const { anchorEl, open, openMenu, closeMenu } = useMenu();

  return (
    <div>
      <IconButton size="small" onClick={openMenu} aria-label="Fahrzeugkalender-Menü öffnen">
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        {calendarViewChoices.map((choice) => (
          <MenuItem
            key={choice.id}
            selected={activeCalendarView === choice.id}
            onClick={() => {
              closeMenu();
              changeCalendarView(choice.id);
            }}
          >
            {choice.name}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}

function _authorizedCreationTabs(vehicle: Vehicle | undefined): Array<"simulatorDrivingLesson" | "vehicleEvent"> {
  const tabs: Array<"simulatorDrivingLesson" | "vehicleEvent"> = [];
  if (isSimulator(vehicle) && grants.has("DrivingLesson:create")) {
    tabs.push("simulatorDrivingLesson");
  }
  if (grants.has("VehicleEvent:create")) {
    tabs.push("vehicleEvent");
  }
  return tabs;
}

function _simulatorAvailabilityEvents(simulator: Simulator, dateRange: DateRange) {
  const startDate = dateRange[0];
  const endDate = dateRange[1];
  const result: Array<EventInput> = [];
  for (let date = startDate; date.valueOf() <= endDate.valueOf(); date = date.nextDay) {
    const weekDay = WeekdayEnum.options[date.toDateTime().weekday - 1];
    for (const [_, from, to] of simulator.availabilities.filter((it) => it[0] === weekDay)) {
      result.push({ display: "background", start: date.at(from).toISO(), end: date.at(to).toISO() });
    }
  }
  return result;
}
